import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, RefreshCw, ArrowRight, CheckCircle, XCircle, Shield, Package, Clock, AlertTriangle, QrCode, Printer, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import QRScanner from '../components/QRScanner';

const AGING_HOURS = 24; // Amber warning if item in same dept > 24 hours

const STATUS_STYLES = {
  received: 'bg-amber-100 text-amber-700',
  dept_holding: 'bg-blue-100 text-blue-700',
  qc_pending: 'bg-purple-100 text-purple-700',
  qc_approved: 'bg-green-100 text-green-700',
  qc_rejected: 'bg-red-100 text-red-700',
  accounting_pending: 'bg-orange-100 text-orange-700',
  accounting_approved: 'bg-teal-100 text-teal-700',
  inward_booked: 'bg-gray-200 text-gray-700',
  returned: 'bg-red-200 text-red-800',
};

const ParcelDetail = () => {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [parcel, setParcel] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Dialogs
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [custodyOpen, setCustodyOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [scanLogOpen, setScanLogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [scanLogs, setScanLogs] = useState([]);
  const [scanVerified, setScanVerified] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'handoff' | 'qc' | 'accounting'

  // Forms
  const [itemForm, setItemForm] = useState({
    item_type: '', description: '', gross_weight: '', net_weight: '',
    pieces: '', carats: '', order_number: '', issue_voucher_id: '',
    supplier_name: '', lot_type: '', notes: '',
  });
  const [handoffForm, setHandoffForm] = useState({ to_person_id: '', to_department: '', notes: '' });
  const [qcForm, setQcForm] = useState({ result: '', weight_verified: '', purity_result: '', grade: '', notes: '' });
  const [custodyLog, setCustodyLog] = useState([]);

  const canEdit = ['admin', 'inventory_dept'].includes(currentUser?.role);
  const canQC = ['admin', 'inventory_dept', 'diamond_dept', 'stone_dept', 'production_supervisor'].includes(currentUser?.role);
  const canAccounting = ['admin', 'cashier'].includes(currentUser?.role);

  const fetchParcel = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get(`/parcels-inward/parcels/${parcelId}`),
        api.get('/parcels-inward/config'),
      ]);
      setParcel(pRes.data);
      setConfig(cRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [parcelId]);

  useEffect(() => { fetchParcel(); }, [fetchParcel]);
  useEffect(() => {
    api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const handleAddItem = async () => {
    if (!itemForm.item_type) { toast.error('Item type required'); return; }
    try {
      const payload = { ...itemForm };
      ['gross_weight', 'net_weight', 'carats'].forEach(k => {
        if (payload[k]) payload[k] = parseFloat(payload[k]);
        else delete payload[k];
      });
      if (payload.pieces) payload.pieces = parseInt(payload.pieces);
      else delete payload.pieces;

      await api.post(`/parcels-inward/parcels/${parcelId}/items`, payload);
      toast.success('Item added');
      setAddItemOpen(false);
      setItemForm({ item_type: '', description: '', gross_weight: '', net_weight: '', pieces: '', carats: '', order_number: '', issue_voucher_id: '', supplier_name: '', lot_type: '', notes: '' });
      fetchParcel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleHandoff = async () => {
    if (!handoffForm.to_person_id) { toast.error('Select recipient'); return; }
    if (!scanVerified) { toast.error('QR scan required before handoff'); return; }
    try {
      await api.post(`/parcels-inward/items/${selectedItem.item_id}/handoff`, {
        ...handoffForm,
        scan_verified: true,
      });
      toast.success('Item transferred');
      setHandoffOpen(false);
      setHandoffForm({ to_person_id: '', to_department: '', notes: '' });
      setScanVerified(false);
      fetchParcel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleQC = async () => {
    if (!qcForm.result) { toast.error('Select QC result'); return; }
    try {
      await api.post(`/parcels-inward/items/${selectedItem.item_id}/qc`, qcForm);
      toast.success(`QC: ${qcForm.result}`);
      setQcOpen(false);
      setQcForm({ result: '', weight_verified: '', purity_result: '', grade: '', notes: '' });
      fetchParcel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAccountingSignoff = async (item) => {
    try {
      await api.post(`/parcels-inward/items/${item.item_id}/accounting-signoff`, { approved: true, notes: '' });
      toast.success('Accounting approved');
      fetchParcel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBookInward = async (item) => {
    try {
      await api.post(`/parcels-inward/items/${item.item_id}/book-inward`, {});
      toast.success('Inward booked');
      fetchParcel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewCustody = async (item) => {
    try {
      const res = await api.get(`/parcels-inward/items/${item.item_id}/custody-log`);
      setCustodyLog(res.data);
      setSelectedItem(item);
      setCustodyOpen(true);
    } catch (e) { toast.error('Failed to load log'); }
  };

  const viewScanLog = async () => {
    try {
      const res = await api.get(`/parcels-inward/parcels/${parcelId}/scan-log`);
      setScanLogs(res.data);
      setScanLogOpen(true);
    } catch (e) { toast.error('Failed to load scan log'); }
  };

  const startScanForAction = (item, action) => {
    setSelectedItem(item);
    setPendingAction(action);
    setScannerOpen(true);
  };

  const handleScanResult = async (decodedText) => {
    setScannerOpen(false);
    // Verify scanned QR matches current parcel
    if (decodedText !== parcel?.parcel_id) {
      toast.error(`Wrong parcel scanned! Expected ${parcel?.parcel_id}, got ${decodedText}`);
      return;
    }
    // Log the scan
    try {
      await api.post('/parcels-inward/scan', { parcel_id: decodedText, action_type: pendingAction || 'lookup' });
    } catch (e) { /* scan log failed, non-blocking */ }

    setScanVerified(true);
    toast.success('QR verified');

    // Open the pending action dialog
    if (pendingAction === 'handoff') {
      setHandoffOpen(true);
    } else if (pendingAction === 'qc') {
      setQcOpen(true);
    } else if (pendingAction === 'accounting') {
      handleAccountingSignoff(selectedItem);
    }
    setPendingAction(null);
  };

  const getAgingHours = (item) => {
    const lastChange = item.last_custody_change || item.updated_at || item.created_at;
    if (!lastChange) return 0;
    const diff = Date.now() - new Date(lastChange).getTime();
    return Math.floor(diff / (1000 * 60 * 60));
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank', 'width=400,height=500');
    const svg = document.getElementById('parcel-qr-svg');
    if (!svg || !printWindow) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    printWindow.document.write(`
      <html><head><title>QR - ${parcel?.parcel_id}</title>
      <style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:sans-serif;}
      h2{margin:0 0 8px;font-size:18px;} p{margin:0;font-size:12px;color:#666;}</style></head>
      <body><h2>${parcel?.parcel_id}</h2><p>${parcel?.sender_name || ''}</p>
      <div style="margin:16px 0">${svgData}</div>
      <p>${new Date().toLocaleDateString('en-IN')}</p>
      <script>setTimeout(()=>window.print(),300);</script></body></html>
    `);
    printWindow.document.close();
  };

  const deptLabels = config.dept_labels || {};
  const itemTypeLabels = config.item_type_labels || {};
  const qcPaths = config.qc_paths || {};
  const items = parcel?.items || [];

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  if (!parcel) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Parcel not found</p>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="parcel-detail-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">{parcel.parcel_id}</h1>
              <p className="text-xs text-emerald-200">{parcel.sender_name} — {parcel.items_count} items</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 h-8 text-xs" onClick={() => setQrOpen(true)} data-testid="show-qr-btn">
              <QrCode className="w-4 h-4" />
            </Button>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[parcel.status] || 'bg-gray-100 text-gray-600'}`}>{parcel.status}</span>
            {canEdit && parcel.status !== 'closed' && (
              <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8" onClick={() => setAddItemOpen(true)} data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Parcel Info */}
      <div className="px-4 pt-3">
        <div className="bg-white rounded-lg border p-3 text-xs text-gray-600 grid grid-cols-2 gap-2">
          <div><span className="text-gray-400">Type:</span> {parcel.sender_type}</div>
          <div><span className="text-gray-400">Received by:</span> {parcel.received_by_name}</div>
          {parcel.courier_name && <div><span className="text-gray-400">Courier:</span> {parcel.courier_name}</div>}
          {parcel.tracking_number && <div><span className="text-gray-400">Tracking:</span> {parcel.tracking_number}</div>}
          {parcel.expected_items_summary && <div className="col-span-2"><span className="text-gray-400">Expected:</span> {parcel.expected_items_summary}</div>}
          {parcel.last_scanned_by_name && (
            <div className="col-span-2 pt-1 border-t mt-1 flex items-center gap-4">
              <span><span className="text-gray-400">Last Scanned By:</span> <span className="font-semibold text-gray-800">{parcel.last_scanned_by_name}</span></span>
              <span><span className="text-gray-400">At:</span> {parcel.last_scanned_at ? new Date(parcel.last_scanned_at).toLocaleString('en-IN') : '-'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{items.length} Item{items.length !== 1 ? 's' : ''}</p>
        {items.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No items added yet. Click "Add Item" to split this parcel.</p>
          </div>
        ) : items.map(item => {
          const path = item.qc_path || [];
          const currentStep = item.current_step || 0;
          const isMyItem = item.current_holder_id === currentUser?.id;
          const hasMismatch = item.reconciliation && !item.reconciliation.matched && item.reconciliation.mismatches?.length > 0;
          const agingHrs = getAgingHours(item);
          const isAging = agingHrs >= AGING_HOURS;

          return (
            <div key={item.item_id} className="bg-white rounded-lg border overflow-hidden" data-testid={`item-${item.item_id}`}>
              {/* Item Header */}
              <div className="px-4 py-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900">{item.item_id}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700">{item.item_type_label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLES[item.status] || ''}`}>{item.status.replace('_', ' ')}</span>
                      {hasMismatch && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 flex items-center gap-0.5">
                          <AlertTriangle className="w-3 h-3" /> MISMATCH
                        </span>
                      )}
                      {isAging && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" /> {agingHrs}h in dept
                        </span>
                      )}
                    </div>
                    {item.description && <p className="text-xs text-gray-600 mt-1">{item.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 flex-wrap">
                      {item.gross_weight && <span>{item.gross_weight}g</span>}
                      {item.carats && <span>{item.carats}ct</span>}
                      {item.pieces && <span>{item.pieces} pcs</span>}
                      {item.order_number && <span>Order: {item.order_number}</span>}
                      {item.supplier_name && <span>Supplier: {item.supplier_name}</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Held by: <span className={`font-medium ${isMyItem ? 'text-emerald-600' : ''}`}>{item.current_holder_name}</span>
                      {' '}@ {deptLabels[item.current_department] || item.current_department}
                    </p>
                  </div>
                </div>

                {/* QC Path Progress */}
                <div className="flex items-center gap-1 mt-3 overflow-x-auto">
                  {path.map((step, i) => (
                    <React.Fragment key={step}>
                      <div className={`flex-shrink-0 px-2 py-1 rounded text-[10px] font-semibold ${
                        i < currentStep ? 'bg-green-100 text-green-700' :
                        i === currentStep ? 'bg-emerald-600 text-white' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {deptLabels[step] || step}
                      </div>
                      {i < path.length - 1 && <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                    </React.Fragment>
                  ))}
                </div>

                {/* Reconciliation Alert */}
                {hasMismatch && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <p className="font-semibold">Reconciliation Mismatch:</p>
                    {item.reconciliation.mismatches.map((m, i) => (
                      <p key={i}>{m.field}: issued {m.original}, returned {m.returned} (diff: {m.difference})</p>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                {item.status !== 'inward_booked' && item.status !== 'returned' && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => viewCustody(item)}>
                      <Clock className="w-3 h-3 mr-1" /> Trail
                    </Button>
                    {isMyItem && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => startScanForAction(item, 'handoff')}>
                        <ScanLine className="w-3 h-3 mr-1" /> Scan & Handoff
                      </Button>
                    )}
                    {canQC && isMyItem && !['qc_approved', 'accounting_approved', 'inward_booked'].includes(item.status) && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px] border-green-300 text-green-600" onClick={() => startScanForAction(item, 'qc')}>
                        <ScanLine className="w-3 h-3 mr-1" /> Scan & QC
                      </Button>
                    )}
                    {canAccounting && item.status === 'qc_approved' && !item.accounting_approved && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px] border-teal-300 text-teal-600" onClick={() => startScanForAction(item, 'accounting')}>
                        <ScanLine className="w-3 h-3 mr-1" /> Scan & Approve
                      </Button>
                    )}
                    {canEdit && item.accounting_approved && item.status !== 'inward_booked' && (
                      <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleBookInward(item)}>
                        Book Inward
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-item-dialog">
          <DialogHeader><DialogTitle>Add Item to Parcel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Item Type *</Label>
              <Select value={itemForm.item_type} onValueChange={v => setItemForm(f => ({...f, item_type: v}))}>
                <SelectTrigger data-testid="item-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(itemTypeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              {itemForm.item_type && (
                <p className="text-xs text-emerald-600 mt-1">
                  QC Path: {(qcPaths[itemForm.item_type] || []).map(s => deptLabels[s] || s).join(' → ')}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={itemForm.description} onChange={e => setItemForm(f => ({...f, description: e.target.value}))} placeholder="Item description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Gross Weight (g)</Label>
                <Input type="number" step="0.001" value={itemForm.gross_weight} onChange={e => setItemForm(f => ({...f, gross_weight: e.target.value}))} placeholder="grams" />
              </div>
              <div className="space-y-1">
                <Label>Pieces</Label>
                <Input type="number" value={itemForm.pieces} onChange={e => setItemForm(f => ({...f, pieces: e.target.value}))} placeholder="count" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Carats</Label>
                <Input type="number" step="0.01" value={itemForm.carats} onChange={e => setItemForm(f => ({...f, carats: e.target.value}))} placeholder="carats" />
              </div>
              <div className="space-y-1">
                <Label>Net Weight (g)</Label>
                <Input type="number" step="0.001" value={itemForm.net_weight} onChange={e => setItemForm(f => ({...f, net_weight: e.target.value}))} placeholder="grams" />
              </div>
            </div>
            {['wip_return'].includes(itemForm.item_type) && (
              <div className="space-y-1">
                <Label>Order Number</Label>
                <Input value={itemForm.order_number} onChange={e => setItemForm(f => ({...f, order_number: e.target.value}))} placeholder="Link to order" />
              </div>
            )}
            {['return_diamond', 'return_stone'].includes(itemForm.item_type) && (
              <div className="space-y-1">
                <Label>Issue Voucher ID (for auto-reconcile)</Label>
                <Input value={itemForm.issue_voucher_id} onChange={e => setItemForm(f => ({...f, issue_voucher_id: e.target.value}))} placeholder="Original issue voucher ID" />
              </div>
            )}
            {['new_diamond', 'new_stone'].includes(itemForm.item_type) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Supplier</Label>
                  <Input value={itemForm.supplier_name} onChange={e => setItemForm(f => ({...f, supplier_name: e.target.value}))} placeholder="Supplier name" />
                </div>
                <div className="space-y-1">
                  <Label>Lot Type</Label>
                  <Select value={itemForm.lot_type} onValueChange={v => setItemForm(f => ({...f, lot_type: v}))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approval">Approval</SelectItem>
                      <SelectItem value="stock">Full Stock</SelectItem>
                      <SelectItem value="assortment">Surat Assortment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={itemForm.notes} onChange={e => setItemForm(f => ({...f, notes: e.target.value}))} placeholder="Any notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemOpen(false)}>Cancel</Button>
            <Button onClick={handleAddItem} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="save-item-btn">Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Handoff Dialog */}
      <Dialog open={handoffOpen} onOpenChange={setHandoffOpen}>
        <DialogContent data-testid="handoff-dialog">
          <DialogHeader><DialogTitle>Transfer Item — {selectedItem?.item_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {scanVerified ? (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                <CheckCircle className="w-4 h-4" /> QR scan verified
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                <AlertTriangle className="w-4 h-4" /> QR scan not verified — handoff blocked
              </div>
            )}
            <div className="space-y-1">
              <Label>Transfer To *</Label>
              <Select value={handoffForm.to_person_id} onValueChange={v => setHandoffForm(f => ({...f, to_person_id: v}))}>
                <SelectTrigger><SelectValue placeholder="Select person" /></SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.id !== currentUser?.id).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.username} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Department</Label>
              {(() => {
                const path = selectedItem?.qc_path || [];
                const step = selectedItem?.current_step || 0;
                const currentDept = selectedItem?.current_department || '';
                const nextDept = step + 1 < path.length ? path[step + 1] : null;
                const allowedDepts = [currentDept];
                if (nextDept) allowedDepts.push(nextDept);
                return (
                  <Select value={handoffForm.to_department} onValueChange={v => setHandoffForm(f => ({...f, to_department: v}))}>
                    <SelectTrigger><SelectValue placeholder="Next department" /></SelectTrigger>
                    <SelectContent>
                      {allowedDepts.map(k => <SelectItem key={k} value={k}>{deptLabels[k] || k}{k === nextDept ? ' (next)' : ' (same)'}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={handoffForm.notes} onChange={e => setHandoffForm(f => ({...f, notes: e.target.value}))} placeholder="Transfer notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoffOpen(false)}>Cancel</Button>
            <Button onClick={handleHandoff} className="bg-[#075E54] hover:bg-[#064E46]" disabled={!scanVerified}>Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QC Dialog */}
      <Dialog open={qcOpen} onOpenChange={setQcOpen}>
        <DialogContent data-testid="qc-dialog">
          <DialogHeader><DialogTitle>QC — {selectedItem?.item_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Result *</Label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${qcForm.result === 'approved' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <input type="radio" name="qc_result" value="approved" checked={qcForm.result === 'approved'} onChange={() => setQcForm(f => ({...f, result: 'approved'}))} className="w-4 h-4 text-green-600" />
                  <CheckCircle className="w-4 h-4 text-green-600" /> <span className="text-sm font-medium">Approved</span>
                </label>
                <label className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${qcForm.result === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                  <input type="radio" name="qc_result" value="rejected" checked={qcForm.result === 'rejected'} onChange={() => setQcForm(f => ({...f, result: 'rejected'}))} className="w-4 h-4 text-red-600" />
                  <XCircle className="w-4 h-4 text-red-600" /> <span className="text-sm font-medium">Rejected</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Weight Verified</Label>
                <Input value={qcForm.weight_verified} onChange={e => setQcForm(f => ({...f, weight_verified: e.target.value}))} placeholder="Verified weight" />
              </div>
              <div className="space-y-1">
                <Label>Grade</Label>
                <Input value={qcForm.grade} onChange={e => setQcForm(f => ({...f, grade: e.target.value}))} placeholder="Quality grade" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={qcForm.notes} onChange={e => setQcForm(f => ({...f, notes: e.target.value}))} placeholder="QC observations" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQcOpen(false)}>Cancel</Button>
            <Button onClick={handleQC} className="bg-[#075E54] hover:bg-[#064E46]">Submit QC</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custody Log Dialog */}
      <Dialog open={custodyOpen} onOpenChange={setCustodyOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Audit Trail — {selectedItem?.item_id}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {custodyLog.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No entries yet</p>
            ) : custodyLog.map((log, i) => (
              <div key={i} className="flex gap-3 text-xs">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1" />
                  {i < custodyLog.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                </div>
                <div className="pb-3">
                  <p className="font-semibold text-gray-800">{log.action.replace('_', ' ')}</p>
                  <p className="text-gray-500">
                    {log.from_person_name && `${log.from_person_name} → `}{log.to_person_name}
                    {log.to_dept && ` (${deptLabels[log.to_dept] || log.to_dept})`}
                  </p>
                  {log.notes && <p className="text-gray-400 italic">{log.notes}</p>}
                  <p className="text-gray-300 mt-0.5">{new Date(log.timestamp).toLocaleString('en-IN')}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Parcel QR Code</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center py-4">
            <QRCodeSVG
              id="parcel-qr-svg"
              value={parcel?.parcel_id || ''}
              size={200}
              level="H"
              includeMargin
            />
            <p className="font-bold text-lg mt-3">{parcel?.parcel_id}</p>
            <p className="text-sm text-gray-500">{parcel?.sender_name}</p>
            <p className="text-xs text-gray-400 mt-1">{new Date().toLocaleDateString('en-IN')}</p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={viewScanLog}>
              <Clock className="w-4 h-4 mr-1" /> Scan Log
            </Button>
            <Button className="flex-1 bg-[#075E54] hover:bg-[#064E46]" onClick={printQR} data-testid="print-qr-btn">
              <Printer className="w-4 h-4 mr-1" /> Print QR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Log Dialog */}
      <Dialog open={scanLogOpen} onOpenChange={setScanLogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Scan Audit Log — {parcel?.parcel_id}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {scanLogs.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No scan events yet</p>
            ) : scanLogs.map((log, i) => (
              <div key={i} className="flex items-center gap-3 text-xs bg-gray-50 rounded-lg p-3">
                <QrCode className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{log.action?.replace('scanned_', '').replace('_', ' ')}</p>
                  <p className="text-gray-500">By: {log.to_person_name}</p>
                </div>
                <p className="text-gray-400 text-[10px]">{new Date(log.timestamp).toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Overlay for custody actions */}
      {scannerOpen && (
        <QRScanner onScan={handleScanResult} onClose={() => { setScannerOpen(false); setPendingAction(null); }} />
      )}
    </div>
  );
};

export default ParcelDetail;
